import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../utils/api';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('ib_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const syncTimer = useRef(null);

  useEffect(() => {
    localStorage.setItem('ib_cart', JSON.stringify(items));
  }, [items]);

  // Sync cart to backend for abandoned cart emails (debounced)
  useEffect(() => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      const token = localStorage.getItem('auth_token');
      if (token && items.length > 0) {
        api.post('/api/ecommerce/cart/save', { items }).catch(() => {});
      }
    }, 5000);
    return () => { if (syncTimer.current) clearTimeout(syncTimer.current); };
  }, [items]);

  const addItem = useCallback((item) => {
    setItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) return prev;
      return [...prev, { ...item, addedAt: new Date().toISOString() }];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    const token = localStorage.getItem('auth_token');
    if (token) {
      api.post('/api/ecommerce/cart/save', { items: [] }).catch(() => {});
    }
  }, []);

  const total = items.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, total, itemCount: items.length }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
}
